import { apiFetch, qs } from '../lib/api';

export type Disposition = 'new' | 'update' | 'duplicate' | 'invalid';
export type ImportSource = 'vehicles' | 'wheels';

export interface ImportJob {
  id: number;
  source_guide: string;
  source: ImportSource | null;
  filename: string;
  status: 'pending' | 'processing' | 'preview' | 'applied' | 'failed' | 'rolled_back';
  row_count: number;
  new_count: number;
  update_count: number;
  duplicate_count: number;
  invalid_count: number;
  dry_run: boolean;
  uploaded_by: string | null;
  s3_key: string | null;
  created_at: string;
  applied_at: string | null;
  // Diagnostic JSONB sidecar (migration 021). For WheelProsInventory rows
  // this carries the InventoryScanDiagnostic shape from fetch-inventory.ts.
  // Other source_guides may use this column too in the future.
  metadata: Record<string, unknown> | null;
  // Decoration added server-side by GET /admin/imports — see
  // src/routes/admin/imports.ts. Optional so older callers keep type-checking.
  job_kind?: 'price_sync' | 'vendor_pull' | 'import';
  health?: PriceSyncHealth | null;
}

export type PriceSyncHealth =
  | 'healthy'
  | 'no_drift'
  | 'partial'
  | 'failed'
  | 'scanned_zero'
  | 'no_variants_returned';

// Shape of metadata.price_shopify_sync written by src/scripts/map-shopify-sync.ts.
// Older rows wrote it under metadata.map_shopify_sync — read both.
export interface PriceSyncSummary {
  dry_run: boolean;
  type: 'wheels' | 'tires' | 'all';
  scanned: number;
  queried: number;
  unchanged: number;
  drift_detected: number;
  applied: number;
  failed: number;
  not_found_in_shopify: number;
  floored_to_map_count: number;
  read_errors: number;
  read_error_batches?: number[];
  unsafe_skipped: number;
  by_product_type?: {
    wheels: { scanned: number; drift: number; applied: number; failed: number; unchanged: number };
    tires:  { scanned: number; drift: number; applied: number; failed: number; unchanged: number };
  };
  by_vendor: Record<string, { drift: number; applied: number; failed: number }>;
  started_at?: string;
  finished_at?: string;
  duration_ms: number;
  health?: PriceSyncHealth;
}

export interface ImportRow {
  id: number;
  row_index: number;
  disposition: Disposition;
  target_id: number | null;
  errors: string[];
  warnings: string[];
  raw_payload: Record<string, unknown>;
  parsed_payload: Record<string, unknown> | null;
  applied?: boolean;
}

export interface ImportJobDetail {
  job: ImportJob;
  samples: Record<Disposition, ImportRow[]>;
}

export interface ImportRowsResponse {
  data: ImportRow[];
}

export function listImports(): Promise<{ data: ImportJob[] }> {
  return apiFetch('/admin/imports');
}

// ----------------------------------------------------------------------
// Imports Dashboard (per-vendor status + data health)
// ----------------------------------------------------------------------

export type SourceStatus = 'OK' | 'Running' | 'Needs Attention' | 'Failed' | 'Never Run' | 'Image Blocked';

export interface SourceRow {
  source: string;
  productType: 'wheels' | 'tires' | 'inventory' | 'vehicles';
  lastCatalogImport: string | null;
  lastInventorySync: string | null;
  lastSuccessful: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
  dbRows: number;
  ready: number;
  pushed: number;
  blocked: number;
  imageMissing: number;
  status: SourceStatus;
}

export function listImportSources(): Promise<{ data: SourceRow[] }> {
  return apiFetch('/admin/imports/sources');
}

export interface HealthCheck {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  count: number;
  detail: string;
  sample: string[];
}

export function getDataHealth(): Promise<{ generatedAt: string; checks: HealthCheck[] }> {
  return apiFetch('/admin/data-health');
}

export interface ScheduleRow {
  name: string;
  schedule: string | null;
  state: 'ENABLED' | 'DISABLED' | null;
  description: string | null;
}

export function listImportSchedules(): Promise<{ data: ScheduleRow[]; note?: string }> {
  return apiFetch('/admin/imports/schedules');
}

// Source-specific vendor fetch — triggered by the "Pull from ATD" / "Pull
// from Wheel Pros" buttons. Returns the new import_job id so the UI can
// link to the detail page. The worker dispatches the vendor fetcher when
// it sees filename='vendor-pull'.
export type VendorPullOption = 'ATD' | 'WheelPros' | 'WheelProsInventory';
export function triggerVendorPull(vendor: VendorPullOption): Promise<{ id: number; vendor: VendorPullOption; status: string }> {
  return apiFetch('/admin/imports/pull', {
    method: 'POST',
    body: JSON.stringify({ vendor }),
  });
}

// Vision Wheel — runs synchronously (CSV download is ~30 sec total). Returns
// the per-reason rejection breakdown plus upsert stats. Same shape for both
// catalog and inventory feeds.
export interface VisionPullSummary {
  feed: 'msrp' | 'inv_update';
  status: 200 | 304;
  raw_rows: number;
  accepted_rows: number;
  rejected_rows: number;
  rejected_by_reason: Record<string, number>;
  upsert_inserted: number;
  upsert_updated: number;
  upsert_bolt_patterns: number;
  last_modified: string | null;
  byte_length: number;
  duration_ms: number;
}
export function triggerVisionCatalogPull(): Promise<VisionPullSummary> {
  return apiFetch('/admin/vision/pull-catalog', { method: 'POST' });
}
export function triggerVisionInventoryPull(): Promise<VisionPullSummary> {
  return apiFetch('/admin/vision/pull-inventory', { method: 'POST' });
}

// Wheel Pros tire enrichment pull. Matches WP tires against existing
// ATD rows (UPC / brand+size+load+speed / brand+size+model) and either
// COALESCEs WP specs in place or inserts standalone vendor='WheelPros'
// rows for unmatched tires (out_of_stock until inventory is wired).
// Toyo is hard-blocked. NEVER pushes to Shopify.
export interface WheelProsTirePullSummary {
  raw_rows: number;
  rejected: {
    missing_sku: number; toyo_blocked: number;
    missing_size: number; unparseable_size: number;
    missing_brand: number; missing_msrp: number;
  };
  toyo_blocked: number;
  matched: { upc: number; brand_size_load_speed: number; brand_size_model: number; total: number };
  unmatched_inserted: number;
  unmatched_skipped_existing_wp: number;
  byte_length: number;
  modify_time: number | null;
  duration_ms: number;
}
export function triggerWheelProsTirePull(): Promise<WheelProsTirePullSummary> {
  return apiFetch('/admin/wheelpros/pull-tires', { method: 'POST' });
}

export function getImport(id: number): Promise<ImportJobDetail> {
  return apiFetch(`/admin/imports/${id}`);
}

export function getImportRows(id: number, params: { disposition?: Disposition; limit?: number; offset?: number }): Promise<ImportRowsResponse> {
  return apiFetch(`/admin/imports/${id}/rows${qs(params as Record<string, unknown>)}`);
}

export function applyImport(id: number): Promise<{ inserted: number; updated: number; skipped: number }> {
  return apiFetch(`/admin/imports/${id}/apply`, { method: 'POST' });
}

// Force-approve an invalid row so the next apply() treats it as 'new'.
// Use when the validator is wrong for this specific vendor row (common case:
// a legitimate bolt pattern not yet in our canonical alias table).
export function approveInvalidRow(jobId: number, rowId: number): Promise<{ approved_row: { id: number; row_index: number } }> {
  return apiFetch(`/admin/imports/${jobId}/rows/${rowId}/approve`, { method: 'POST' });
}

export function approveAllInvalid(jobId: number): Promise<{ approved: number }> {
  return apiFetch(`/admin/imports/${jobId}/approve-all-invalid`, { method: 'POST' });
}

// Upload a WheelPros per-SKU-per-location inventory CSV (exported from
// their dealer portal). Returns per-bucket stats (same-day / next-day /
// standard / out-of-stock) so the admin can see what the apply did.
//
// Routed through apiFetch — apiFetch detects FormData bodies and skips the
// Content-Type override so the browser sets the multipart boundary
// correctly, AND we get the auto-401-refresh path that the raw `fetch()`
// upload was missing (uploads after the 60s session-token TTL silently 401-ed).
export async function uploadWheelProsInventory(file: File): Promise<{
  ok: true;
  stats: {
    rows_read: number;
    skus_matched: number;
    skus_not_found: number;
    wheels_updated: number;
    same_day_count: number;
    next_day_count: number;
    standard_count: number;
    out_of_stock_count: number;
    non_numeric_columns_ignored: string[];
  };
}> {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch('/admin/imports/wheelpros-inventory', {
    method: 'POST',
    body: fd,
  });
}

export async function uploadImport(
  file: File,
  source: ImportSource,
  vendor?: string,
): Promise<{ id: number; status: string }> {
  const fd = new FormData();
  fd.append('source', source);
  if (vendor) fd.append('vendor', vendor);
  fd.append('file', file);
  return apiFetch('/admin/imports', {
    method: 'POST',
    body: fd,
  });
}
